import { useCallback } from 'react'
import logger from '../utils/logger'

/**
 * Custom hook for handling realtime choice and poll update events
 * @param {Function} setChoices - Function to update choices state
 * @param {Function} setPoll - Function to update poll state (optional)
 * @param {string} pollId - The poll ID for logging
 * @returns {Object} Choice and poll event handlers
 */
function useRealtimeChoices(setChoices, setPoll, pollId) {
    const handleNewChoice = useCallback((newChoice) => {
        setChoices(prev => {
            // Check if choice already exists (avoid duplicates)
            if (prev.some(choice => choice.id === newChoice.id)) {
                logger.warn('Realtime: Choice already exists, skipping', { choiceId: newChoice.id })
                return prev
            }

            const choiceWithCount = {
                ...newChoice,
                vote_count: newChoice.vote_count || 0,
                upvote_count: newChoice.upvote_count || 0,
                downvote_count: newChoice.downvote_count || 0
            }

            const updated = [...prev, choiceWithCount]

            // Sort by vote count DESC, then created_at ASC
            updated.sort((a, b) => {
                if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            })

            return updated
        })
    }, [setChoices])

    const handleChoiceDeleted = useCallback((choiceId) => {
        setChoices(prev => {
            const exists = prev.some(choice => choice.id === choiceId)
            if (!exists) {
                logger.warn('Realtime: Choice deletion for unknown choice, skipping', { choiceId })
                return prev
            }

            logger.info('Realtime: Removing deleted choice from list', { choiceId })
            return prev.filter(choice => choice.id !== choiceId)
        })
    }, [setChoices])

    const handleChoiceUpdated = useCallback((updatedChoice) => {
        setChoices(prev => {
            const exists = prev.some(choice => choice.id === updatedChoice.id)
            if (!exists) {
                logger.warn('Realtime: Choice update for unknown choice, skipping', { choiceId: updatedChoice.id })
                return prev
            }

            logger.info('Realtime: Updating choice text', { choiceId: updatedChoice.id, newText: updatedChoice.text?.substring(0, 30) })
            return prev.map(choice =>
                choice.id === updatedChoice.id
                    ? { ...choice, text: updatedChoice.text, creator_device_id: updatedChoice.creator_device_id }
                    : choice
            )
        })
    }, [setChoices])

    const handlePollUpdated = useCallback((updatedPoll) => {
        if (!setPoll) return

        logger.info('Realtime: Updating poll', { pollId: updatedPoll.id, newTitle: updatedPoll.title?.substring(0, 30) })
        setPoll(prev => ({
            ...prev,
            title: updatedPoll.title
        }))
    }, [setPoll])

    return {
        handleNewChoice,
        handleChoiceDeleted,
        handleChoiceUpdated,
        handlePollUpdated
    }
}

export default useRealtimeChoices
